import React, { FC, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Heading from './Heading';
import { FamiliarData, PersonalData } from '../../types/PersonalData';
import { useAuthStore as useAuthStoreEroski } from '../../store/AuthContext';
import { StorageHelper } from '../../utils/StorageHelper';
import { MisDatosConstants } from '../../utils/MisDatosConstants';

type Props = {
  loadModal: (data: PersonalData) => void;
  getData: () => void;
  isFamilyData: boolean;
  personalData: PersonalData;
  familiarData: FamiliarData;
  promiseApi: boolean;
};
const PersonalDataView: FC<Props> = ({
  loadModal,
  personalData,
  isFamilyData,
  getData,
  familiarData,
  promiseApi,
}) => {
  const dataUser = useAuthStoreEroski();
  const { t } = useTranslation();
  const [isUserAditional, setIsUserAditional] = useState<boolean>(false);
  const [isUserTitular, setIsUserTitular] = useState<boolean>(false);
  const loadModalData = () => {
    loadModal(personalData);
  };
  useEffect(() => {
    if (dataUser.user.token !== '') {
      getData();
      setIsUserAditional(dataUser.user.adicional || false);
      setIsUserTitular(dataUser.user.titular || false);
    }
    const whoAskedChange = sessionStorage.getItem(
      MisDatosConstants?.WHO_ASKED_CHANGED,
    );
    if (
      whoAskedChange !== null &&
      whoAskedChange === MisDatosConstants.EMAIL_CONTACT_CHANGED
    ) {
      loadModalData();
    }
    if (
      whoAskedChange !== null &&
      whoAskedChange === MisDatosConstants.TELEFONO_EMAIL_CONTACT_CHANGED
    ) {
      sessionStorage.removeItem(MisDatosConstants?.WHO_ASKED_CHANGED);
      loadModalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser.user.token]);

  useEffect(() => {
    if (!promiseApi) {
      const oroSource = StorageHelper.getOpenOroPersonalData();
      if (oroSource !== null && oroSource === 'true') {
        loadModal(personalData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promiseApi]);
  const getTipoDocLabel = (id: any) => {
    if (id === 1 || id === '1') return t('datos.personaldata.label-dni');
    if (id === 2 || id === '2') return t('datos.personaldata.label-pasaporte');
    if (id === 3 || id === '3') return t('datos.personaldata.label-nie');
    if (id === 4 || id === '4') return t('datos.personaldata.label-cif');
    return t('datos.personaldata.label-otros');
  };

  const getIdiomaCompleto = (idioma: string) => {
    switch (idioma) {
      case 'T':
        return t('datos.personaldata.catalan');
      case 'E':
        return t('datos.personaldata.euskera');
      case 'GAL':
        return t('datos.personaldata.gallego');
      case 'I':
        return t('datos.personaldata.ingles');
      case 'A':
        return t('datos.personaldata.aleman');
      default:
        return t('datos.personaldata.espaniol');
    }
  };

  const getFamiliaNumerosa = (exist: boolean) => {
    return exist ? t('global.positive') : t('global.negative');
  };
  return (
    <>
      <div className="bloque-datos">
        <Heading
          tipoUsuario={personalData.tipoUsuario}
          setShow={loadModalData}
        />

        <div className="bloque-datos__datos">
          {/* eslint-disable-next-line no-nested-ternary */}
          {personalData.tipoUsuario !== 2 &&
          personalData.nombre &&
          personalData.apellido1 ? (
            <h3 className="bloque-datos__name">
              {personalData.nombre.toUpperCase()}{' '}
              {personalData.apellido1.toUpperCase()}{' '}
              {personalData.apellido2
                ? personalData.apellido2.toUpperCase()
                : ''}
            </h3>
          ) : personalData.tipoUsuario === 2 && personalData.razonSocial ? (
            <h3 className="bloque-datos__name">{personalData.razonSocial}</h3>
          ) : (
            ''
          )}

          <ul className="bloque-datos__list">
            {personalData.tipoUsuario === 2 && personalData.nombre && (
              <li>
                <p>
                  {t('datos.personaldata.contactName')}:{' '}
                  <strong> {personalData.nombre} </strong>
                </p>
              </li>
            )}
            {personalData.tipoUsuario === 2 && personalData.apellido1 && (
              <li>
                <p>
                  {t('datos.personaldata.contactLastName1')}:{' '}
                  <strong> {personalData.apellido1} </strong>
                </p>
              </li>
            )}
            {personalData.tipoUsuario !== 2 &&
              personalData.fechaNacimiento !== null && (
                <li>
                  <p>
                    {t('datos.personaldata.bornDate')}:{' '}
                    <strong>
                      {moment(
                        personalData.fechaNacimiento,
                        'DD MM YYYY',
                      ).format('DD/MM/YYYY')}
                    </strong>
                  </p>
                </li>
              )}
            {personalData.idDocumento && personalData.dniEditable && (
              <li>
                <p>
                  {getTipoDocLabel(personalData.tipoDocumento).toUpperCase()}:{' '}
                  <strong>{personalData.idDocumento}</strong>
                </p>
              </li>
            )}
            {personalData.tipoUsuario !== 2 && personalData.genero && (
              <li>
                <p>
                  {t('datos.personaldata.genre')}:{' '}
                  <strong>
                    {personalData.genero === 'H'
                      ? t('datos.personaldata.male')
                      : t('datos.personaldata.female')}
                  </strong>
                </p>
              </li>
            )}
            {personalData.idiomaComunicacion && (
              <li>
                <p>
                  {t('datos.personaldata.language')}:{' '}
                  <strong>
                    {getIdiomaCompleto(personalData.idiomaComunicacion)}
                  </strong>
                </p>
              </li>
            )}
            {isFamilyData && isUserTitular && !isUserAditional && (
              <li>
                <p>
                  {t('datos.personaldata.family')}:{' '}
                  <strong>
                    {getFamiliaNumerosa(familiarData.esFamiliaNumerosa)}
                  </strong>
                </p>
              </li>
            )}
            {isUserTitular &&
              !isUserAditional &&
              isFamilyData &&
              familiarData.numeroCarnet && (
                <li>
                  <p>
                    {t('datos.personaldata.carne')}:{' '}
                    <strong>{familiarData.numeroCarnet}</strong>
                  </p>
                </li>
              )}
            {isUserTitular &&
              !isUserAditional &&
              isFamilyData &&
              familiarData.fechaCaducidad && (
                <li>
                  <p>
                    {t('datos.personaldata.expirationDate')}:{' '}
                    <strong>{familiarData.fechaCaducidad}</strong>
                  </p>
                </li>
              )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default PersonalDataView;
